const text = (value) => (value == null ? "" : String(value).trim());

const cloneList = (value) =>
  Array.isArray(value)
    ? value.map((item) =>
        item && typeof item === "object" ? { ...item } : item,
      )
    : [];

export const toDateInput = (value) => {
  const raw = text(value);
  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) return raw.slice(0, 10);
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";
  const year = parsed.getFullYear();
  const month = String(parsed.getMonth() + 1).padStart(2, "0");
  const day = String(parsed.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export const toTimeInput = (value) => {
  const raw = text(value);
  const match = raw.match(/(?:T|\s|^)(\d{1,2}):(\d{2})/);
  if (match) {
    return `${match[1].padStart(2, "0")}:${match[2]}`;
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";
  return `${String(parsed.getHours()).padStart(2, "0")}:${String(
    parsed.getMinutes(),
  ).padStart(2, "0")}`;
};

export const normalizeOrderContractType = (value) => {
  const normalized = text(value)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
  if (normalized === "interim") return "Intérim";
  if (normalized === "vacation") return "Vacation";
  if (normalized === "cdd") return "CDD";
  if (normalized === "cdi") return "CDI";
  return text(value);
};

export const serializeOrderContact = (contact) => {
  if (typeof contact === "string") return contact.trim();
  return [
    contact?.firstname,
    contact?.lastname,
    contact?.email,
    contact?.mobile || contact?.phone,
  ]
    .filter(Boolean)
    .join(" ");
};

const normalizePeriod = (period, fallback = {}) => {
  const startDate = toDateInput(
    period?.startDate || period?.date || fallback.startDate,
  );
  return {
    startDate,
    endDate: toDateInput(
      period?.endDate || period?.date || fallback.endDate || startDate,
    ),
    startHour: toTimeInput(period?.startHour || fallback.startHour),
    endHour: toTimeInput(period?.endHour || fallback.endHour),
  };
};

const orderPeriods = (order) => {
  const fallback = {
    startDate: order?.startDate,
    endDate: order?.endDate,
    startHour: order?.startHour,
    endHour: order?.endHour,
  };
  if (Array.isArray(order?.dateRanges) && order.dateRanges.length) {
    return order.dateRanges.map((period) => normalizePeriod(period, fallback));
  }
  const selectedDates = Array.isArray(order?.selectedDates)
    ? order.selectedDates
    : [];
  if (selectedDates.length) {
    return selectedDates.map((date) =>
      normalizePeriod({ startDate: date, endDate: date }, fallback),
    );
  }
  return [normalizePeriod(fallback)];
};

export const createOrderFormInitialState = (order = null) => {
  const source = order || {};
  const clientRecord =
    source.client && typeof source.client === "object" ? source.client : null;
  const clientId = clientRecord?._id || source.client || "";
  const companyName =
    clientRecord?.companyName || source.companyName || source.clientName || "";
  const periods = orderPeriods(source);
  const firstPeriod = periods[0] || {};

  return {
    clientRecord,
    clientSearch: companyName,
    extraPeriods: periods.slice(1),
    diplomes: cloneList(source.diplomes || source.diplomas),
    habilitations: cloneList(source.habilitations),
    caces: cloneList(source.caces || source.caceses),
    permis: cloneList(source.permis || source.licences),
    formData: {
      client: clientId ? { _id: text(clientId), name: companyName } : null,
      startHour: firstPeriod.startHour || toTimeInput(source.startHour),
      endHour: firstPeriod.endHour || toTimeInput(source.endHour),
      contact: serializeOrderContact(source.contact),
      workedDays: cloneList(source.workedDays),
      nbHeureHebdo: text(source.nbHeureHebdo),
      justificatif: text(source.justificatif),
      startDate: firstPeriod.startDate || toDateInput(source.startDate),
      endDate: toDateInput(source.endDate || firstPeriod.endDate),
      clientName: text(source.clientName || companyName),
      address: text(source.address),
      postCode: text(source.postCode),
      town: text(source.town),
      hourlyRate: text(source.hourlyRate),
      typeContrat: normalizeOrderContractType(source.typeContrat),
      motif: text(source.motif),
      primes: cloneList(source.primes),
      consigne: text(source.consigne) || "0 : Aucune",
      isVehicule: Boolean(source.isVehicule),
      epis: cloneList(source.epis),
      epiProvider: text(source.epiProvider),
      facturation: cloneList(source.facturation),
      quantity: Number(source.quantity) > 0 ? Number(source.quantity) : 1,
      qualification: text(source.qualification),
      task: text(source.task),
    },
  };
};

export const clientAddressOptions = (client) => {
  if (!client) return [];
  return [
    {
      key: "siege",
      value: "siege",
      fields: [client.address, client.postCode, client.town],
      label: [client.address, client.postCode, client.town]
        .filter(Boolean)
        .join(" "),
    },
    {
      key: "livraison",
      value: "livraison",
      fields: [
        client.addressLivraison,
        client.postCodeLivraison,
        client.townLivraison,
      ],
      label: [
        "Adresse de livraison :",
        client.addressLivraison,
        client.postCodeLivraison,
        client.townLivraison,
      ]
        .filter(Boolean)
        .join(" "),
    },
    {
      key: "facturation",
      value: "facturation",
      fields: [
        client.addressFacturation,
        client.postCodeFacturation,
        client.townFacturation,
      ],
      label: [
        "Adresse de facturation :",
        client.addressFacturation,
        client.postCodeFacturation,
        client.townFacturation,
      ]
        .filter(Boolean)
        .join(" "),
    },
  ]
    .filter((option) => option.fields.some(Boolean))
    .map(({ fields, ...option }) => option);
};

export const clientContactOptions = (
  client,
  canUse = () => true,
  selectedValue = "",
) => {
  const options = (client?.contacts || [])
    .filter(canUse)
    .map((contact, index) => ({
      value: serializeOrderContact(contact),
      label:
        [
          contact.firstname,
          contact.lastname,
          contact.email && `(${contact.email})`,
        ]
          .filter(Boolean)
          .join(" ") || `Contact ${index + 1}`,
      key: contact._id || `contact-${index}`,
    }))
    .filter((option) => option.value);

  if (
    selectedValue &&
    !options.some((option) => option.value === selectedValue)
  ) {
    options.unshift({
      value: selectedValue,
      label: selectedValue,
      key: "contact-initial",
    });
  }
  return options;
};

export const buildOrderPayload = ({
  formData,
  diplomes,
  habilitations,
  caces,
  permis,
  extraPeriods,
}) => {
  const typeContrat = normalizeOrderContractType(formData.typeContrat);
  const multipleDates = ["Vacation", "CDD"].includes(typeContrat);
  const periods = [
    {
      startDate: toDateInput(formData.startDate),
      endDate: toDateInput(formData.startDate),
      startHour: toTimeInput(formData.startHour),
      endHour: toTimeInput(formData.endHour),
    },
    ...cloneList(extraPeriods).map((period) => normalizePeriod(period)),
  ];
  const selectedDates = multipleDates
    ? periods.map((period) => period.startDate).filter(Boolean)
    : [];

  return {
    address: text(formData.address),
    client: text(formData.client?._id || formData.client),
    clientName: text(formData.clientName),
    companyName: text(formData.client?.name || formData.clientName),
    contact: serializeOrderContact(formData.contact),
    endHour: toTimeInput(formData.endHour),
    task: text(formData.task),
    justificatif: text(formData.justificatif),
    epiProvider: text(formData.epiProvider),
    epis: cloneList(formData.epis),
    primes: cloneList(formData.primes),
    workedDays: cloneList(formData.workedDays),
    consigne: text(formData.consigne),
    facturation: cloneList(formData.facturation),
    hourlyRate: text(formData.hourlyRate),
    isVehicule: Boolean(formData.isVehicule),
    motif: text(formData.motif),
    postCode: text(formData.postCode),
    qualification: text(formData.qualification),
    quantity: Number(formData.quantity),
    startDate: toDateInput(formData.startDate),
    endDate: multipleDates
      ? selectedDates[0] || null
      : typeContrat === "CDI"
        ? null
        : toDateInput(formData.endDate),
    selectedDates,
    dateRanges: multipleDates ? periods : [],
    startHour: toTimeInput(formData.startHour),
    town: text(formData.town),
    typeContrat,
    habilitations: cloneList(habilitations),
    diplomes: cloneList(diplomes),
    caces: cloneList(caces),
    permis: cloneList(permis),
    nbHeureHebdo: text(formData.nbHeureHebdo),
  };
};

export const persistOrder = (api, mode, orderId, payload) => {
  if (mode === "edit") {
    if (!orderId) throw new Error("Identifiant de commande manquant");
    return api.put(`order/${orderId}`, payload);
  }
  return api.post("order/freelance", payload);
};

const relationIds = (order) =>
  new Set(
    [
      "contactedCandidates",
      "refusedByAgence",
      "acceptedCandidates",
      "confirmedCandidates",
      "submittedToClient",
      "contractedCandidates",
      "refusedCandidates",
    ]
      .flatMap((key) => order?.[key] || [])
      .map((entry) => text(entry?._id || entry)),
  );

export const availableOrderCandidates = (
  order,
  potentialCandidates = [],
  fallback = {},
  trusted = false,
) => {
  const excluded = relationIds(order);
  const prefix = text(order?.postCode || fallback.postCode).slice(0, 2);
  return (Array.isArray(potentialCandidates) ? potentialCandidates : []).filter(
    (candidate) => {
      if (excluded.has(text(candidate?._id))) return false;
      return (
        trusted ||
        !prefix ||
        (candidate.locations || candidate.zones || []).some((location) =>
          text(location?.label || location).startsWith(prefix),
        )
      );
    },
  );
};

export const hydrateOrderResponse = (data, fallback = {}) => {
  const order = data?.order || data || {};
  const potentialCandidates = Array.isArray(data?.potentialCandidates)
    ? data.potentialCandidates
    : Array.isArray(order.candidates)
      ? order.candidates
      : fallback.candidates || [];
  const candidates = availableOrderCandidates(
    order,
    potentialCandidates,
    fallback,
    data?.candidatesFiltered === true,
  );

  return {
    ...fallback,
    ...order,
    candidates,
  };
};
